/**
 * The renderer: `(site, page, language) → a React tree`.
 *
 * Pure. One code path for every site on the platform — a "template" is data, so there is no per-site
 * build and no forking. The same component renders the editor's preview and the static HTML that
 * gets published, which is what makes "what you see is what publishes" true by construction rather
 * than by discipline.
 *
 * ⚠️ THE TYPE→COMPONENT MAP IS THE ONE PLACE RENDER COMPONENTS ARE KEYED, and it is deliberately
 * separate from `@ksa/model`'s registry, which is keyed the same way by `type`. Keeping the two maps
 * apart is what lets the API and the Worker import the registry without importing React.
 */

import type { ReactNode } from 'react'
import {
  dirFor,
  effectiveWidth,
  getBlockDef,
  widthBasis,
  widthMin,
  type Block,
  type Lang,
  type Page,
  type Site,
} from '@ksa/model'
import { RenderProvider, type RenderMode } from './context.tsx'
import { getTemplate, templateVars } from './tokens.ts'
import { Navbar } from './blocks/navbar.tsx'
import { Footer } from './blocks/footer.tsx'
import { Hero } from './blocks/hero.tsx'
import { HeadingBlock } from './blocks/heading.tsx'
import { TextSection } from './blocks/text.tsx'
import { ImageBlock } from './blocks/image.tsx'
import { GalleryBlock } from './blocks/gallery.tsx'
import { FeaturesBlock } from './blocks/features.tsx'
import { TeamBlock } from './blocks/team.tsx'
import { CtaBlock } from './blocks/cta.tsx'
import { ContactBlock } from './blocks/contact.tsx'
import { FaqBlock } from './blocks/faq.tsx'
import { TestimonialsBlock } from './blocks/testimonials.tsx'

const COMPONENTS: Record<string, (props: { block: Block }) => ReactNode> = {
  navbar: Navbar,
  footer: Footer,
  hero: Hero,
  heading: HeadingBlock,
  text: TextSection,
  image: ImageBlock,
  gallery: GalleryBlock,
  features: FeaturesBlock,
  team: TeamBlock,
  cta: CtaBlock,
  contact: ContactBlock,
  faq: FaqBlock,
  testimonials: TestimonialsBlock,
}

/** Every block type this renderer can draw. Exported so a test can assert it matches the registry —
 *  a registry entry with no component is a block a customer can add and never see. */
export const RENDERABLE_TYPES = Object.keys(COMPONENTS)

/**
 * A block whose type this renderer has never heard of.
 *
 * ⚠️ IT MUST NOT CRASH THE PAGE, AND IT MUST NOT APPEAR ON A PUBLISHED ONE. Both halves matter and
 * they point in different directions. A published site is a SNAPSHOT: a document authored against a
 * newer registry, or one restored from a backup, must still render — a thrown error inside one block
 * would take a live customer's whole homepage down. But a live customer's homepage is also not the
 * place to display our schema versions to their visitors, so on the publish path it renders nothing
 * at all, and in the editor it renders a marker the customer can act on.
 */
function UnknownBlock({ block, mode }: { block: Block; mode: RenderMode }) {
  if (mode === 'publish') return null
  return <div className="s-unknown">{`Unsupported block: ${block.type}`}</div>
}

function RenderBlock({ block, mode }: { block: Block; mode: RenderMode }) {
  const Component = COMPONENTS[block.type]
  if (!Component) return <UnknownBlock block={block} mode={mode} />
  return <Component block={block} />
}

export type SitePageProps = {
  site: Site
  page: Page
  lang: Lang
  mode: RenderMode
  mediaUrl: (key: string) => string
  /** Where the builder lives, for the footer's report link. Defaulted in `RenderProvider`, so the
   *  editor preview does not have to know or care. */
  builderOrigin?: string
}

/**
 * The page's own content — navbar, the flat block flow, footer.
 *
 * Kept separate from the document shell (`html.tsx`) so the editor can mount exactly this inside a
 * preview pane, while publishing wraps the same tree in `<html>`. Two callers, one tree.
 */
export function SitePage({ site, page, lang, mode, mediaUrl, builderOrigin }: SitePageProps) {
  const template = getTemplate(site.template)

  return (
    <RenderProvider site={site} page={page} lang={lang} mode={mode} mediaUrl={mediaUrl} builderOrigin={builderOrigin}>
      <div
        // Marked so a test can tell the CUSTOMER's site apart from the builder's own chrome. They
        // have different owners and different palettes, and confusing them is how a preview ends up
        // looking like an admin panel.
        data-testid="site-preview"
        className="s-root"
        /**
         * ⚠️ THE ROOT DECLARES ITS OWN DIRECTION, and that was missing. On a published page `<html>`
         * carries it, so nothing was visibly wrong there — but in the EDITOR this tree is mounted
         * inside an application that may be running in the other direction, and without this the
         * preview of an Arabic page laid out left-to-right. The customer would have been shown a
         * page that is not what publishes, which is the one thing this renderer exists to prevent.
         * On the published page it is a harmless duplicate of what `<html>` already says.
         */
        dir={dirFor(lang)}
        lang={lang}
        style={templateVars(template) as React.CSSProperties}
      >
        <RenderBlock block={site.navbar} mode={mode} />

        {/* The flat flow. Each block takes its share of the row; a full block fills the line and
            pushes the next one below it, while two halves flow up beside each other. Because the row
            is direction-neutral, halves MIRROR under RTL with no second rule — the DOM order does
            not change, which is the entire point of the flat model. */}
        <main className="s-flow">
          {page.blocks.map((b) => {
            const w = effectiveWidth(b, getBlockDef(b.type))
            return (
              // The block id is the anchor target, so a link that says "scroll to the contact
              // section" resolves against a real element.
              <div
                key={b.id}
                id={b.id}
                style={{ flexBasis: widthBasis(w), minWidth: widthMin(w) }}
              >
                <RenderBlock block={b} mode={mode} />
              </div>
            )
          })}
        </main>

        <RenderBlock block={site.footer} mode={mode} />
      </div>
    </RenderProvider>
  )
}
